import {
  CONVERSION_ATTRIBUTION_ACQUISITION_FIELDS,
  CONVERSION_ATTRIBUTION_ACQUISITION_PARAMS,
  CONVERSION_ATTRIBUTION_ACQUISITION_VALUE_SCHEMAS,
  CONVERSION_ATTRIBUTION_PUSH_KEYS,
  CONVERSION_ATTRIBUTION_URL_PARAMS,
  conversionAttributionIdSchema,
  conversionAttributionSchema,
  type ConversionAttribution,
  type ConversionAttributionAcquisitionField,
  type ConversionAttributionSource,
} from './schema';

/** Why an attempted capture produced no record. */
export type ConversionAttributionRejection = 'missing_campaign_id' | 'invalid_value';
export type ConversionAttributionReason = 'none' | ConversionAttributionRejection;

/**
 * The single predicate every capture surface shares: `attempted` is true iff
 * the input carried at least one attribution key. An ordinary link or a plain
 * push is not an attempt and emits no signal. `reason` says what was dropped,
 * so the operational signal can say why: on a rejected attempt it names why
 * nothing was kept, and on a capture it names a value that was dropped while
 * the rest was kept (the Iterable pair's reason first, then `invalid_value`
 * for a dropped acquisition value, otherwise `none`).
 */
export interface ConversionAttributionEvaluation {
  readonly attempted: boolean;
  readonly record: ConversionAttribution | null;
  readonly reason: ConversionAttributionReason;
}

const NOT_ATTEMPTED: ConversionAttributionEvaluation = {
  attempted: false,
  record: null,
  reason: 'none',
};

function rejected(reason: ConversionAttributionRejection): ConversionAttributionEvaluation {
  return { attempted: true, record: null, reason };
}

/**
 * A raw value read off the input before validation. `undefined` = key absent;
 * `INVALID` = key present with a type no id can be made from (object, boolean,
 * non-finite number). Both branches must be distinguishable so a present-but-
 * unusable value is a rejection rather than a silent miss.
 */
const INVALID: unique symbol = Symbol('invalid');
type RawId = string | undefined | typeof INVALID;

interface RawPair {
  readonly campaignId: RawId;
  readonly templateId: RawId;
}

type RawAcquisition = Readonly<Record<ConversionAttributionAcquisitionField, RawId>>;

const NO_ACQUISITION: RawAcquisition = {
  utmSource: undefined,
  utmMedium: undefined,
  utmCampaign: undefined,
  utmContent: undefined,
  utmTerm: undefined,
  experimentArm: undefined,
  linkRef: undefined,
};

type PairOutcome =
  | { readonly kind: 'absent' }
  | { readonly kind: 'kept'; readonly campaignId: string; readonly templateId: string | null }
  | { readonly kind: 'dropped'; readonly reason: ConversionAttributionRejection };

/**
 * The Iterable pair is all-or-nothing: a template id is meaningless without
 * its campaign, and a half-valid pair is dropped whole rather than forwarded
 * as a partial send identifier.
 */
function evaluatePair(raw: RawPair): PairOutcome {
  if (raw.campaignId === undefined && raw.templateId === undefined) return { kind: 'absent' };
  if (
    raw.campaignId === undefined ||
    (raw.campaignId !== INVALID && raw.campaignId.trim() === '')
  ) {
    return { kind: 'dropped', reason: 'missing_campaign_id' };
  }
  if (raw.campaignId === INVALID) return { kind: 'dropped', reason: 'invalid_value' };
  const campaign = conversionAttributionIdSchema.safeParse(raw.campaignId);
  if (!campaign.success) return { kind: 'dropped', reason: 'invalid_value' };

  if (raw.templateId === INVALID) return { kind: 'dropped', reason: 'invalid_value' };
  if (raw.templateId === undefined || raw.templateId.trim() === '') {
    return { kind: 'kept', campaignId: campaign.data, templateId: null };
  }
  const template = conversionAttributionIdSchema.safeParse(raw.templateId);
  if (!template.success) return { kind: 'dropped', reason: 'invalid_value' };
  return { kind: 'kept', campaignId: campaign.data, templateId: template.data };
}

/**
 * Evaluate a capture. Acquisition values are independent of each other and of
 * the pair: each is validated on its own and an invalid one is dropped while
 * the rest are kept, so a malformed `utm_term` never costs the experiment arm.
 */
function evaluate(
  pair: RawPair,
  acquisition: RawAcquisition,
  source: ConversionAttributionSource,
  capturedAt: string,
): ConversionAttributionEvaluation {
  const pairOutcome = evaluatePair(pair);
  const acquisitionAttempted = CONVERSION_ATTRIBUTION_ACQUISITION_FIELDS.some(
    (field) => acquisition[field] !== undefined,
  );
  if (pairOutcome.kind === 'absent' && !acquisitionAttempted) return NOT_ATTEMPTED;

  let acquisitionDropped = false;
  const values: Record<ConversionAttributionAcquisitionField, string | null> = {
    utmSource: null,
    utmMedium: null,
    utmCampaign: null,
    utmContent: null,
    utmTerm: null,
    experimentArm: null,
    linkRef: null,
  };
  for (const field of CONVERSION_ATTRIBUTION_ACQUISITION_FIELDS) {
    const raw = acquisition[field];
    if (raw === undefined) continue;
    if (raw === INVALID) {
      acquisitionDropped = true;
      continue;
    }
    // A present-but-blank param (`?utm_term=`) fails the bounded grammar like
    // any other bad value, so it is dropped and surfaced, never silently missed.
    const parsed = CONVERSION_ATTRIBUTION_ACQUISITION_VALUE_SCHEMAS[field].safeParse(raw);
    if (parsed.success) values[field] = parsed.data;
    else acquisitionDropped = true;
  }

  const reason: ConversionAttributionReason =
    pairOutcome.kind === 'dropped'
      ? pairOutcome.reason
      : acquisitionDropped
        ? 'invalid_value'
        : 'none';

  const record = conversionAttributionSchema.safeParse({
    campaignId: pairOutcome.kind === 'kept' ? pairOutcome.campaignId : null,
    templateId: pairOutcome.kind === 'kept' ? pairOutcome.templateId : null,
    ...values,
    source,
    capturedAt,
  });
  if (!record.success) return rejected(reason === 'none' ? 'invalid_value' : reason);
  return { attempted: true, record: record.data, reason };
}

// ── URL ────────────────────────────────────────────────────────────────────

export type SearchParamsInput = URLSearchParams | Readonly<Record<string, string | undefined>>;

function readParam(params: SearchParamsInput, key: string): RawId {
  if (params instanceof URLSearchParams) {
    return params.has(key) ? (params.get(key) ?? '') : undefined;
  }
  return Object.prototype.hasOwnProperty.call(params, key) ? params[key] : undefined;
}

/**
 * Evaluate a params object from any capture source: the Iterable pair plus
 * the acquisition params, and nothing else. A new source (an install
 * provider's first-open params, for one) calls this with its own `source`
 * rather than growing a parallel evaluator.
 */
export function evaluateConversionAttributionFromParams(
  params: SearchParamsInput,
  source: ConversionAttributionSource,
  capturedAt: string,
): ConversionAttributionEvaluation {
  return evaluate(
    {
      campaignId: readParam(params, CONVERSION_ATTRIBUTION_URL_PARAMS.campaignId),
      templateId: readParam(params, CONVERSION_ATTRIBUTION_URL_PARAMS.templateId),
    },
    readAcquisition(params),
    source,
    capturedAt,
  );
}

function readAcquisition(params: SearchParamsInput): RawAcquisition {
  const acquisition: Record<ConversionAttributionAcquisitionField, RawId> = { ...NO_ACQUISITION };
  for (const field of CONVERSION_ATTRIBUTION_ACQUISITION_FIELDS) {
    acquisition[field] = readParam(params, CONVERSION_ATTRIBUTION_ACQUISITION_PARAMS[field]);
  }
  return acquisition;
}

/** Evaluate a landing URL's query: the params evaluator with source `url`. */
export function evaluateConversionAttributionFromSearchParams(
  params: SearchParamsInput,
  capturedAt: string,
): ConversionAttributionEvaluation {
  return evaluateConversionAttributionFromParams(params, 'url', capturedAt);
}

/**
 * Evaluate an attributed install's first-open parameters (a flat string map
 * read from the install-attribution provider). They carry the same keys a
 * landing URL does (the Iterable pair and the acquisition params), so they go
 * through the same params evaluator, stamped `install`. Any key later taught
 * to the params evaluator is captured from installs too.
 */
export function evaluateConversionAttributionFromInstallParams(
  params: Readonly<Record<string, string>>,
  capturedAt: string,
): ConversionAttributionEvaluation {
  return evaluateConversionAttributionFromParams(params, 'install', capturedAt);
}

export function extractConversionAttributionFromSearchParams(
  params: SearchParamsInput,
  capturedAt: string,
): ConversionAttribution | null {
  return evaluateConversionAttributionFromSearchParams(params, capturedAt).record;
}

// Scheme-agnostic query split. `new URL()` diverges between Node and Hermes on
// non-special (custom) schemes — the deeplink parser pre-parses them with a
// regex for the same reason — so the query string is lifted textually here
// and handed to URLSearchParams, which both runtimes agree on.
const URL_QUERY_RE = /^[a-z][a-z0-9+.-]*:\/\/[^?#]*(?:\?([^#]*))?/i;

/**
 * The query string of a URL (without `?`), `''` when the URL has none, or
 * `null` when the input is not URL-shaped at all.
 */
export function queryStringOfUrl(url: string): string | null {
  const match = URL_QUERY_RE.exec(url);
  if (!match) return null;
  return match[1] ?? '';
}

export function evaluateConversionAttributionFromUrl(
  url: string,
  capturedAt: string,
): ConversionAttributionEvaluation {
  const query = queryStringOfUrl(url);
  if (query === null) return NOT_ATTEMPTED;
  return evaluateConversionAttributionFromSearchParams(new URLSearchParams(query), capturedAt);
}

export function extractConversionAttributionFromUrl(
  url: string,
  capturedAt: string,
): ConversionAttribution | null {
  return evaluateConversionAttributionFromUrl(url, capturedAt).record;
}

// ── Push ───────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readPushId(container: Record<string, unknown>, key: string): RawId {
  if (!Object.prototype.hasOwnProperty.call(container, key)) return undefined;
  const value = container[key];
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value)) {
    return String(value);
  }
  return INVALID;
}

/**
 * Evaluate a push notification's `data` payload. Iterable's `itbl` block takes
 * precedence; a Galapagos push may carry the same ids at the top level. A
 * payload with neither shape (today's `intentId` + `definitionKey`) is not an
 * attempt.
 *
 * Acquisition values are never read from the payload itself. When the tap also
 * carries a deeplink, pass it as `deeplinkUrl`: a payload pair that is kept
 * then takes the link's acquisition params too, so the payload staying the
 * authoritative source for the tap does not cost the link's `utm_*`,
 * `exp_arm`, or `link_ref`. Without a kept pair the link is left alone, and the
 * caller reads it as a URL landing as before.
 */
export function evaluateConversionAttributionFromPushData(
  data: unknown,
  capturedAt: string,
  deeplinkUrl?: string | null,
): ConversionAttributionEvaluation {
  if (!isRecord(data)) return NOT_ATTEMPTED;
  const pair = readPushPair(data);
  return evaluate(pair, acquisitionForPushPair(pair, deeplinkUrl), 'push', capturedAt);
}

function readPushPair(data: Record<string, unknown>): RawPair {
  const {
    block,
    campaignId: campaignKey,
    templateId: templateKey,
  } = CONVERSION_ATTRIBUTION_PUSH_KEYS;

  const itbl = data[block];
  if (isRecord(itbl)) {
    const campaignId = readPushId(itbl, campaignKey);
    const templateId = readPushId(itbl, templateKey);
    if (campaignId !== undefined || templateId !== undefined) return { campaignId, templateId };
  }
  return { campaignId: readPushId(data, campaignKey), templateId: readPushId(data, templateKey) };
}

function acquisitionForPushPair(pair: RawPair, deeplinkUrl?: string | null): RawAcquisition {
  if (!deeplinkUrl || evaluatePair(pair).kind !== 'kept') return NO_ACQUISITION;
  const query = queryStringOfUrl(deeplinkUrl);
  if (query === null) return NO_ACQUISITION;
  return readAcquisition(new URLSearchParams(query));
}

export function extractConversionAttributionFromPushData(
  data: unknown,
  capturedAt: string,
): ConversionAttribution | null {
  return evaluateConversionAttributionFromPushData(data, capturedAt).record;
}
